import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, Plus, PencilSimple } from "@phosphor-icons/react";

const empty = { title: "", message: "", active: true, style: "information", link_url: "", link_label: "", starts_at: "", ends_at: "" };

const STYLE_LABELS = { information: "Information", promotion: "Promotion", alert: "Important Alert" };
const STYLE_BADGE = {
  information: "bg-brand-blue/10 text-brand-blue",
  promotion: "bg-brand-purple/10 text-brand-purple",
  alert: "bg-brand-teal/10 text-brand-teal",
};

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...a }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.message) { toast.error("Title and message are required"); return; }
    const payload = {
      title: form.title, message: form.message, active: form.active, style: form.style || "information",
      link_url: form.link_url || "", link_label: form.link_label || "",
      starts_at: form.starts_at || "", ends_at: form.ends_at || "",
    };
    try {
      if (editId) await api.patch(`/admin/announcements/${editId}`, payload);
      else await api.post("/admin/announcements", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`); load(); toast.success("Deleted");
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Announcements</h1>
        <Button onClick={openNew} data-testid="add-announcement" className="rounded-full bg-brand-purple text-white hover:bg-brand-purple2">
          <Plus size={18} className="mr-1.5" /> Add Announcement
        </Button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Website announcements you can activate or deactivate.</p>

      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-slate-400">No announcements yet.</p>}
        {items.map((a) => (
          <div key={a.id} className="flex items-start justify-between rounded-2xl border border-slate-200 bg-white p-6">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-semibold text-slate-900">{a.title}</h3>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STYLE_BADGE[a.style] || STYLE_BADGE.information}`}>{STYLE_LABELS[a.style] || "Information"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{a.active ? "Active" : "Inactive"}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{a.message}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(a)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-blue"><PencilSimple size={16} /></button>
              <button onClick={() => remove(a.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="an-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="an-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Style</Label>
              <Select value={form.style || "information"} onValueChange={(v) => setForm({ ...form, style: v })}>
                <SelectTrigger data-testid="an-style" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="information">Information (blue)</SelectItem>
                  <SelectItem value="promotion">Promotion (purple)</SelectItem>
                  <SelectItem value="alert">Important Alert (teal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Link URL (optional)</Label><Input data-testid="an-link-url" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="/book-a-ride" /></div>
              <div className="space-y-1.5"><Label>Link Label</Label><Input data-testid="an-link-label" value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} placeholder="Book now" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start Date (optional)</Label><Input data-testid="an-starts" type="date" value={form.starts_at || ""} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End Date (optional)</Label><Input data-testid="an-ends" type="date" value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-500">Leave dates blank to show immediately with no expiry. Only active announcements within the date window appear on the public site.</p>
            <div className="flex items-center justify-between"><Label>Active</Label><Switch data-testid="an-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
            <Button onClick={save} data-testid="an-save" className="w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple2">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
